import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, PhoneCall, MessageSquare, Upload, FileText, Image as ImageIcon, Check, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

function JoinPage() {
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('Pro');

  const totalSteps = 5;
  const progress = onboardingStep > 0 ? (onboardingStep / totalSteps) * 100 : 0;

  const navigate = useNavigate();

  const plans = [
    {
      name: 'Basic',
      price: '₹999',
      description: 'Perfect for getting started',
      features: ['5 messages/month', 'Basic listing', 'Network access', 'Email support'],
      recommended: false
    },
    {
      name: 'Pro',
      price: '₹2,499',
      description: 'Most popular choice',
      features: ['25 messages/month', 'Priority listing', 'Full network access', 'Priority support', 'Analytics dashboard'],
      recommended: true
    },
    {
      name: 'Premium',
      price: '₹4,999',
      description: 'For maximum visibility',
      features: ['Unlimited messages', 'Featured listing', 'Premium placement', '24/7 dedicated support', 'Advanced analytics', 'Marketing tools'],
      recommended: false
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to payment instructions page with selected plan
    navigate('/payment-instructions', { state: { planName: selectedPlan } });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-2xl w-full" data-testid="success-message">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h2>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for your interest in joining Travelodges. Our team will review your application and contact you within 24-48 hours.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-emerald-900 mb-3">What happens next?</h3>
              <ul className="text-left space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>We will verify your property details</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Our team will contact you for a brief consultation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>You will receive your dashboard access within 48 hours</span>
                </li>
              </ul>
            </div>
            <Button 
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.location.href = '/'}
            >
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-b" data-testid="join-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Join Travelodges</h1>
          <p className="text-xl text-gray-600">List your property and start receiving quality referrals</p>
        </div>
      </section>

      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Options Sidebar */}
            <div className="md:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Need Help?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Contact Us</h4>
                    <div className="space-y-3">
                      <a href="mailto:travelodgesnetwork@gmail.com" className="flex items-center space-x-3 text-gray-600 hover:text-emerald-600 transition-colors">
                        <Mail className="h-5 w-5" />
                        <span>travelodgesnetwork@gmail.com</span>
                      </a>
                      <a href="tel:+918499092521" className="flex items-center space-x-3 text-gray-600 hover:text-emerald-600 transition-colors">
                        <PhoneCall className="h-5 w-5" />
                        <span>+91 8499092521</span>
                      </a>
                      <a href="https://wa.me/918499092521" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-gray-600 hover:text-emerald-600 transition-colors">
                        <MessageSquare className="h-5 w-5" />
                        <span>WhatsApp Support</span>
                      </a>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <h4 className="font-semibold text-gray-900 mb-3">Why Join?</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>No commission on bookings</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>500+ partner network</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>45% average growth</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>30-day free trial</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Onboarding Form */}
            <div className="md:col-span-2">
              <Card data-testid="onboarding-form">
                <CardHeader className="border-b">
                  <div className="flex justify-between items-center mb-4">
                    <CardTitle className="text-2xl">Property Onboarding</CardTitle>
                    {onboardingStep > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setOnboardingStep(0)}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                  {onboardingStep > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Step {onboardingStep} of {totalSteps}</span>
                        <span>{Math.round(progress)}% Complete</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-6">
                  {onboardingStep === 0 && (
                    <div className="space-y-6 text-center py-8">
                      <h3 className="text-2xl font-bold text-gray-900">Welcome to Travelodges!</h3>
                      <p className="text-gray-600 max-w-lg mx-auto">
                        Let's get your property listed. This will only take 5-10 minutes. You can save and continue later.
                      </p>
                      <Button 
                        size="lg"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setOnboardingStep(1)}
                        data-testid="start-onboarding"
                      >
                        Start Application
                        <ChevronRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  )}

                  {onboardingStep === 1 && (
                    <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); setOnboardingStep(2); }}>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Property Information</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Property Name *</label>
                            <Input 
                              type="text" 
                              placeholder="Enter your property name" 
                              required
                              data-testid="property-name"
                            />
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Property Type *</label>
                              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" required data-testid="property-type">
                                <option value="">Select type</option>
                                <option>Wildlife Resort</option>
                                <option>Hill Station Homestay</option>
                                <option>Beach Resort</option>
                                <option>Boutique Hotel</option>
                                <option>Experiential Stay</option>
                                <option>Farm Stay & Retreat</option>
                                <option>Eco-Lodge</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Number of Rooms *</label>
                              <Input 
                                type="number" 
                                placeholder="e.g., 15" 
                                required
                                data-testid="room-count"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">City *</label>
                              <Input 
                                type="text" 
                                placeholder="City" 
                                required
                                data-testid="city"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">State *</label>
                              <Input 
                                type="text" 
                                placeholder="State" 
                                required
                                data-testid="state"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Property Description *</label>
                            <textarea 
                              rows="4" 
                              placeholder="Describe your property, unique features, and guest experience..." 
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                              required
                              data-testid="description"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between pt-6 border-t">
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setOnboardingStep(0)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="next-step-1"
                        >
                          Next Step
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}

                  {onboardingStep === 2 && (
                    <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); setOnboardingStep(3); }}>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Amenities & Pricing</h3>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium mb-3">Select Amenities</label>
                            <div className="grid md:grid-cols-3 gap-3">
                              {['WiFi', 'Pool', 'Restaurant', 'Spa', 'Gym', 'Parking', 'Room Service', 'Conference Hall', 'Garden', 'Pet Friendly', 'Adventure Sports', 'Nature Walks'].map((amenity) => (
                                <label key={amenity} className="flex items-center space-x-2 p-3 border rounded-lg hover:border-emerald-500 cursor-pointer">
                                  <input type="checkbox" className="w-4 h-4 text-emerald-600 rounded" />
                                  <span className="text-sm">{amenity}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Price Range (per night) *</label>
                            <div className="grid md:grid-cols-2 gap-4">
                              <Input 
                                type="number" 
                                placeholder="Minimum Rate (₹)" 
                                required
                                data-testid="min-rate"
                              />
                              <Input 
                                type="number" 
                                placeholder="Maximum Rate (₹)" 
                                required
                                data-testid="max-rate"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between pt-6 border-t">
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setOnboardingStep(1)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="next-step-2"
                        >
                          Next Step
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}

                  {onboardingStep === 3 && (
                    <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); setOnboardingStep(4); }}>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Photos & Documents</h3>
                        <div className="space-y-4">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer">
                            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm font-medium mb-1">Upload Property Photos</p>
                            <p className="text-xs text-gray-500 mb-3">PNG, JPG up to 10MB (minimum 5 photos)</p>
                            <Button type="button" variant="outline" size="sm" data-testid="upload-photos">
                              <Upload className="h-4 w-4 mr-2" />
                              Choose Files
                            </Button>
                          </div>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors cursor-pointer">
                            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm font-medium mb-1">Upload Documents</p>
                            <p className="text-xs text-gray-500 mb-3">Trade license, GST certificate (PDF)</p>
                            <Button type="button" variant="outline" size="sm" data-testid="upload-docs">
                              <Upload className="h-4 w-4 mr-2" />
                              Choose Files
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between pt-6 border-t">
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setOnboardingStep(2)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="next-step-3"
                        >
                          Next Step
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}

                  {onboardingStep === 4 && (
                    <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); setOnboardingStep(5); }}>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Contact Details</h3>
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Contact Person *</label>
                              <Input 
                                type="text" 
                                placeholder="Full name" 
                                required
                                data-testid="contact-name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Designation *</label>
                              <Input 
                                type="text" 
                                placeholder="e.g., Owner, Manager" 
                                required
                                data-testid="designation"
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Email *</label>
                              <Input 
                                type="email" 
                                placeholder="your@email.com" 
                                required
                                data-testid="email"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Phone *</label>
                              <Input 
                                type="tel" 
                                placeholder="+91 XXXXX XXXXX" 
                                required
                                data-testid="phone"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Website (Optional)</label>
                            <Input 
                              type="url" 
                              placeholder="https://yourwebsite.com" 
                              data-testid="website"
                            />
                          </div>
                          <label className="flex items-start space-x-3">
                            <input type="checkbox" className="w-5 h-5 text-emerald-600 rounded mt-1" required data-testid="terms-checkbox" />
                            <span className="text-sm text-gray-600">
                              I agree to the Terms & Conditions and Privacy Policy. I confirm that all information provided is accurate.
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-between pt-6 border-t">
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setOnboardingStep(3)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          Continue to Plan Selection
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}

                  {onboardingStep === 5 && (
                    <form className="space-y-6" onSubmit={handleSubmit}>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Choose Your Subscription Plan</h3>
                        <p className="text-gray-600 mb-6">Select the plan that best fits your resort's needs</p>
                        
                        <div className="grid md:grid-cols-3 gap-4">
                          {plans.map((plan) => (
                            <div 
                              key={plan.name}
                              className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                                selectedPlan === plan.name 
                                  ? 'border-emerald-600 bg-emerald-50' 
                                  : 'border-gray-200 hover:border-emerald-300'
                              }`}
                              onClick={() => setSelectedPlan(plan.name)}
                            >
                              {plan.recommended && (
                                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-emerald-600 text-white">
                                  Most Popular
                                </Badge>
                              )}
                              <div className="text-center mb-4">
                                <h4 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                                <div className="text-3xl font-bold text-emerald-600 mb-1">{plan.price}</div>
                                <div className="text-sm text-gray-600">/month</div>
                                <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
                              </div>
                              <ul className="space-y-3 mb-4">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start space-x-2 text-sm">
                                    <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-700">{feature}</span>
                                  </li>
                                ))}
                              </ul>
                              <div className="flex items-center justify-center">
                                <input
                                  type="radio"
                                  name="plan"
                                  value={plan.name}
                                  checked={selectedPlan === plan.name}
                                  onChange={() => setSelectedPlan(plan.name)}
                                  className="w-5 h-5 text-emerald-600"
                                  required
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                          <h4 className="font-semibold text-blue-900 mb-2">30-Day Free Trial</h4>
                          <p className="text-sm text-blue-800">Start with a free trial on any plan. No credit card required. You'll only be charged after your trial period ends.</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-6 border-t">
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setOnboardingStep(4)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="submit-application"
                        >
                          Continue to Payment
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JoinPage;